import campaignsJson from './fixtures/campaigns.json'
import type { Ad, Campaign, CreativeFilter, CreativeLibraryItem, LibraryCreative, Product, Risk, Violation } from './types'

interface CampaignFixture {
  products: Product[]
}

const campaignFixture = campaignsJson as unknown as CampaignFixture
const productsById = new Map(campaignFixture.products.map((product) => [product.id, product]))

export function selectCreativeLibrary(
  creatives: LibraryCreative[],
  ads: Ad[],
  campaigns: Campaign[],
  violations: Violation[],
  risks: Risk[],
  filter: CreativeFilter = {},
): CreativeLibraryItem[] {
  const adById = new Map(ads.map((ad) => [ad.id, ad]))
  const violationCreativeIds = new Set(violations
    .filter((violation) => violation.state !== 'resolved')
    .map((violation) => adById.get(violation.adId)?.creativeId)
    .filter(Boolean))
  const riskCampaignIds = new Set(risks.filter((risk) => risk.state === 'open').map((risk) => risk.campaignId))
  const riskProductIds = new Set(campaigns.filter((campaign) => riskCampaignIds.has(campaign.id)).map((campaign) => campaign.productId))
  const usedByCreative = ads.reduce<Map<string, number>>((counts, ad) => {
    counts.set(ad.creativeId, (counts.get(ad.creativeId) ?? 0) + 1)
    return counts
  }, new Map())
  const normalizedQuery = filter.q?.trim().toLocaleLowerCase('zh-Hans') ?? ''

  return creatives.reduce<CreativeLibraryItem[]>((items, creative) => {
    const usedByAds = usedByCreative.get(creative.id) ?? 0
    if (filter.source && filter.source !== 'all' && creative.source !== filter.source) return items
    if (filter.kind && filter.kind !== 'all' && creative.kind !== filter.kind) return items
    if (filter.productId && filter.productId !== 'all' && creative.productId !== filter.productId) return items
    if (filter.usage === 'used' && usedByAds === 0) return items
    if (filter.usage === 'unused' && usedByAds > 0) return items
    const productName = productsById.get(creative.productId)?.name ?? ''
    if (normalizedQuery && !`${creative.name}\n${creative.kol ?? ''}\n${productName}`.toLocaleLowerCase('zh-Hans').includes(normalizedQuery)) return items
    const compliance = creative.compliance?.level === 'violation'
      ? (violationCreativeIds.has(creative.id) ? creative.compliance : null)
      : creative.compliance?.level === 'risk'
        ? (riskProductIds.has(creative.productId) ? creative.compliance : null)
        : null
    items.push({ ...creative, compliance, usedByAds })
    return items
  }, [])
}
